import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

export interface MonteCarloResult {
  id: string;
  contrato_id: string;
  baseline_id: string | null;
  n_iteracoes: number;
  distribuicao: string;
  fonte_dados: string;
  p10_dias: number;
  p50_dias: number;
  p80_dias: number;
  p90_dias: number;
  media_dias: number;
  desvio_padrao_dias: number;
  p10_data: string;
  p50_data: string;
  p80_data: string;
  p90_data: string;
  prob_on_time: number;
  prob_apos_baseline: number | null;
  delay_recovery_index: number | null;
  janela_incerteza_dias: number | null;
  data_convergencia: string | null;
  tendencia_prazo: string | null;
  drivers_risco: Array<{ fator: string; impacto: string; severidade: string }>;
  benchmark_usado: boolean;
  benchmark_peso: number;
  input_snapshot: Record<string, unknown>;
  status: string;
  triggered_by: string | null;
  versao_logica: number;
  created_at: string;
}

export function useEmpMonteCarloLatest(contratoId: string | null) {
  return useQuery({
    queryKey: ['emp-montecarlo-latest', contratoId],
    queryFn: async (): Promise<MonteCarloResult | null> => {
      if (!contratoId) return null;
      const { data, error } = await supabase
        .from('emp_forecast_montecarlo' as any)
        .select('*')
        .eq('contrato_id', contratoId)
        .eq('status', 'ativo')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) { console.error('[MC] Error:', error); return null; }
      return data as unknown as MonteCarloResult | null;
    },
    enabled: !!contratoId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useEmpMonteCarloHistory(contratoId: string | null) {
  return useQuery({
    queryKey: ['emp-montecarlo-history', contratoId],
    queryFn: async (): Promise<MonteCarloResult[]> => {
      if (!contratoId) return [];
      const { data, error } = await supabase
        .from('emp_forecast_montecarlo' as any)
        .select('*')
        .eq('contrato_id', contratoId)
        .in('status', ['ativo', 'historico'])
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) { console.error('[MC] History error:', error); return []; }
      return (data ?? []) as unknown as MonteCarloResult[];
    },
    enabled: !!contratoId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRunMonteCarlo() {
  const { toast } = useToast();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: { contratoId: string; triggeredBy?: string }) => {
      // Get current user for audit trail
      const { data: { user } } = await supabase.auth.getUser();
      
      const resp = await supabase.functions.invoke('forecast-montecarlo', {
        body: {
          contrato_id: params.contratoId,
          triggered_by: params.triggeredBy || 'manual',
          created_by: user?.id || null,
        },
      });
      // Handle rate-limit (429) with user-friendly message
      if (resp.data?.rate_limited) {
        const secs = resp.data.retry_after_seconds ?? 60;
        throw new Error(`Limite de simulações atingido. Tente novamente em ${secs} segundos.`);
      }
      if (resp.error) throw new Error(resp.data?.error || resp.error.message);
      if (!resp.data?.success) throw new Error(resp.data?.error || 'Simulação falhou');
      return resp.data;
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({ queryKey: ['emp-montecarlo-latest', params.contratoId] });
      qc.invalidateQueries({ queryKey: ['emp-montecarlo-history', params.contratoId] });
      qc.invalidateQueries({ queryKey: ['curva-s-fisica', params.contratoId] });
      toast({ title: 'Simulação Monte Carlo concluída', description: `Tendência: ${_data.tendencia_prazo}` });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro na simulação MC', description: err.message, variant: 'destructive' });
    },
  });
}
